import {
  DiagnoseAntrag,
  Domaene,
  EinsatzgebieteTier,
  FachbausteinAntrag,
  Feature,
  GegeDatenAntrag,
  Jagdbehoerde,
  Jagdschein as BffJagdschein,
  JhpDatenAntrag,
  KennzeichnungsArt,
  Kuendiger,
  Meldebehoerde,
  PhDatenAntrag,
  Produktlinienname,
  RsDatenAntrag,
  RsVermietungDatenAntrag,
  SicherungsglaeubigerAntrag as BffSicherungsglaeubiger,
  SparteAntrag,
  SpartenType,
  ThhHundDatenAntrag,
  ThhPferdDatenAntrag,
  TierAntrag,
  Tierart,
  Tiergeschlecht,
  TierV2Antrag,
  TkvDatenAntrag,
  TkvV2DatenAntrag,
  UnderwriterDecisionAntrag,
  UriDatenAntrag,
  VermieteteWohnung as BffVermieteteWohnung,
  Vertragsgrund,
  VhvDatenAntrag,
  VhvV2DatenAntrag,
  GegenstandCategory,
  VorschadenArt as BffVorschadenArt,
  VorversicherungAntrag as BffVorversicherung,
  VwgDatenAntrag,
  WartezeitverzichtAntrag,
} from '@ps20/bff-models';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import dayjs from 'dayjs';
import {
  App,
  areAppTypesEquivalent,
  compareProductIdsIfExistingAndSpLaufnrNotExists,
  FremdVNVorvertrag,
  getAppEnumByAppName,
  getAppEnumBySpartenType,
  getSpartenTypeByApp,
  TkvV2Vorbelegungdaten,
  VnWechselGrund
} from '@ps20-frontend/frontend-models';
import {
  foldUploadedFileIds,
  buildUwWireDecision,
  findUwVereinbarungText,
  findUwZuschlagProzent,
  getAllUwContexts,
  ProductUw,
  selectUwContextsForSparte,
  selectUwEntries,
  UwContext,
  UwSubmissionField,
  UwContextDescriptor,
  UwContextEntry,
  UwSharedDecision,
} from '@ps20-frontend/uw';
import { convertDate, DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { selectFeatureToggle, selectPreisverzeichnisTestValue } from '@ps20-frontend/selector';
import { PS20Logger } from '@ps20-frontend/logging';
import { VorschadenArt } from '@ps20-frontend/shop-integration-redux';
import { VwgV2DatenAntrag } from '@ps20/bff-models/dist/lib/ps20-bff-model-antrag';
import { selectSicherungsglaeubigerForAngebot } from '../../selector/sicherungsglaeubiger.selector';
import {
  GegeCheckoutProdukt,
  Produkt,
  RsCheckoutProdukt,
  RsVermietungCheckoutProdukt,
  Sicherungsglaeubiger,
  VhvCheckoutProdukt,
  VhvV2CheckoutProdukt,
  VwgCheckoutProdukt
} from '../../model';
import { ThhPferdAngaben, ThhPferdGeschlecht } from '../../reducer/thhPferdAngaben.reducer';
import { Vorversicherung, VorversicherungsItem } from '../../model/vorversicherung';
import { VermieteteWohnung } from '../../reducer/vermietete-wohnungen.reducer';
import { MeldebehoerdeAngaben } from '../../reducer/meldebehoerdeAngaben-reducer';
import { ThhHundGeschlecht } from '../../reducer/thhHundAngaben.reducer';
import { Kennzeichnungen, TkvAngabenElement } from '../../reducer/tkvAngaben.reducer';
import { getVhvCheckoutProdukt, selectGegeProdukte, showTkvDiagnosen } from '../../selector/produkt.selector';
import { Jagdschein, JhpAngaben } from '../../reducer/jhpAngaben.reducer';
import { VpAngaben } from '../../reducer/unfAngaben.reducer';
import { getVhvDocumentsIds } from '../checkFilesAfterLoadSaga';
import { HoeherwertigerHausratUtils } from '../../../utils/hoeherwertiger-hausrat.utils';
import { TkvAngabenV2Item } from '../../reducer/tkvAngabenV2.reducer';
import { HealthStatusEntry } from '../../model/healthStatus';
import { hasGefahrenInVorversicherung, isVorversicherungVorhanden } from '../../selector/vorversicherung.selector';

export const DIAGNOSE_SONSTIGES = '10503';
const hoeherwertigerHausratUtils = new HoeherwertigerHausratUtils();

export function createSparten(vorschlagdaten: SparteAntrag[], state: IAppState): SparteAntrag[] {
  return state.produkte.map(produkt => {
    const sparteAntrag = addDataFromCheckoutToVorschlagsdatenOfProdukt(
      produkt,
      vorschlagdaten,
      state
    );
    if (selectFeatureToggle(state, Feature.PVZ_ERSATZGESCHAEFT_MOCK)) {
      sparteAntrag.preisverzeichnis = selectPreisverzeichnisTestValue(state);
    }
    logMissingPreisverzeichnis(sparteAntrag, produkt);
    return sparteAntrag;
  });
}

function logMissingPreisverzeichnis(sparteAntrag: SparteAntrag, produkt: Produkt): void {
  if (sparteAntrag.preisverzeichnis != null) {
    return;
  }
  PS20Logger.error(
    App.SHOP_CHECKOUT,
    'Einreichen.preisverzeichnisMissing',
    JSON.stringify({
      appId: produkt.appId,
      angebotsId: produkt.angebotsId,
      spartenType: sparteAntrag.spartenType,
      produktline: sparteAntrag.produkt?.produktline,
      kurztext: sparteAntrag.produkt?.kurztext,
      beibehalten: produkt.beibehalten,
      hasVorvertrag: !!sparteAntrag.vorvertragsnummern?.length,
    }),
  );
}

function addDataFromCheckoutToVorschlagsdatenOfProdukt(
  produkt: Produkt,
  vorschlagdaten: SparteAntrag[],
  state: IAppState
): SparteAntrag {
  const vorschlagsdatenForProdukt = getVorschlagsdatenForProdukt(produkt, vorschlagdaten);

  vorschlagsdatenForProdukt.vorvertragSofortschutz = produkt.vorvertragSofortschutz;
  vorschlagsdatenForProdukt.zuschlagRisiko = produkt.zuschlagRisiko;
  addBeginnAblaufToVorschlagsdaten(vorschlagsdatenForProdukt, produkt);
  addVorversicherungenToVorschlagsdaten(
    state.vorversicherungen,
    produkt,
    vorschlagsdatenForProdukt
  );
  addSpartenSpecificDataToVorschlagsdaten(vorschlagsdatenForProdukt, produkt, state);
  addZielmarktInfoToVorschlagsdaten(vorschlagsdatenForProdukt, produkt);
  addMaklerToVoschlagsdaten(vorschlagsdatenForProdukt, state.vermittler.fremdvorgangsnummer);
  if (!areAppTypesEquivalent(produkt.appId, App.VWG)) {
    vorschlagsdatenForProdukt.vorvertragSofortschutzAblaufDatum =
      produkt.vorvertragSofortschutzAblaufDatum;
  }

  if (
    vorschlagsdatenForProdukt.vorvertragsnummern &&
    vorschlagsdatenForProdukt.vorvertragsnummern.length > 0
  ) {
    addVnWechselGrundToVorvertragsnummern(vorschlagsdatenForProdukt, state);
  }
  return vorschlagsdatenForProdukt;
}

function getVorschlagsdatenForProdukt(
  produkt: Produkt,
  vorschlagdaten: SparteAntrag[]
): SparteAntrag | undefined {
  return vorschlagdaten.find(
    vorschlagsdatenOfSparte =>
      vorschlagsdatenOfSparte.spartenType === getSpartenTypeByApp(produkt.appId) &&
      vorschlagsdatenOfSparte.angebotsId === produkt.angebotsId
  );
}

function addBeginnAblaufToVorschlagsdaten(
  vorschlagsdatenForProdukt: SparteAntrag,
  produkt: Produkt
): void {
  vorschlagsdatenForProdukt.produkt.beginn = dayjs(produkt.beginn, DATEFORMAT_LOCAL_STATE).format(
    DATEFORMAT_SHORT_ISO
  );
  vorschlagsdatenForProdukt.produkt.ablauf = dayjs(produkt.ablauf, DATEFORMAT_LOCAL_STATE).format(
    DATEFORMAT_SHORT_ISO
  );
}

function addMaklerToVoschlagsdaten(
  vorschlagsdatenForProdukt: SparteAntrag,
  fremdvorgangsnummer: string
): void {
  vorschlagsdatenForProdukt.produkt.maklerInfo =
    fremdvorgangsnummer === '' ? null : fremdvorgangsnummer;
}

function addVorversicherungenToVorschlagsdaten(
  vorversicherungen: Vorversicherung[],
  produkt: Produkt,
  vorschlagsdatenForProdukt: SparteAntrag
): void {
  // Don't send vorversicherung data for Ersatzgeschäft (except hasGefahrenInVorversicherung) products (products with vorvertragsnummer)
  if (
    produkt.vorvertragsnummer?.length &&
    hasGefahrenInVorversicherung(produkt, vorversicherungen) !== true
  ) {
    vorschlagsdatenForProdukt.vorversicherung = [];
    return;
  }

  if (hasGefahrenInVorversicherung(produkt, vorversicherungen) && isVorversicherungVorhanden(produkt, vorversicherungen) !== true) {
    vorschlagsdatenForProdukt.vorversicherung = [];
    return;
  }

  const vorversicherung = vorversicherungen.find(
    elem =>
      elem.appId === getAppEnumByAppName(produkt.appId) && elem.angebotsId === produkt.angebotsId
  );

  vorschlagsdatenForProdukt.vorversicherung =
    vorversicherung == null
      ? []
      : convertToBffVorversicherungen(vorversicherung.vorversicherungsListe);
}

function applicableUwDescriptors(spartenType: SpartenType): UwContextDescriptor[] {
  return selectUwContextsForSparte(getAllUwContexts(), spartenType);
}

export function handleVwgVorschlagsdaten(
  state: IAppState,
  produkt: Produkt,
  vorschlagsdatenForProdukt: SparteAntrag
): void {
  const vwgAntrag =
    vorschlagsdatenForProdukt.spartenType === SpartenType.WOHNGEBAEUDE ?
      vorschlagsdatenForProdukt as VwgDatenAntrag : vorschlagsdatenForProdukt as VwgV2DatenAntrag;

  const sicherungsglaebigerForAngebot: Array<Sicherungsglaeubiger> =
    selectSicherungsglaeubigerForAngebot(state.sicherungsglaeubiger, produkt.angebotsId);
  vwgAntrag.sicherungsglaeubiger = convertToBffSicherungsglaeubiger(sicherungsglaebigerForAngebot);
  const vwgCheckoutProdukt = produkt as VwgCheckoutProdukt;
  const isVwgV2 = vorschlagsdatenForProdukt.spartenType === SpartenType.WOHNGEBAEUDE_V2;
  const uwDescriptors = applicableUwDescriptors(vorschlagsdatenForProdukt.spartenType);
  const uwEntries = vwgCheckoutProdukt.uw?.entries ?? [];

  vwgAntrag.uploadedFilesIds = foldUploadedFileIds(uwEntries, uwDescriptors);
  const naturalHazardsEntry = selectUwEntries(vwgCheckoutProdukt.uw, UwContext.NATURAL_HAZARDS)[0];
  vwgAntrag.elementarfragebogenFileToAnalyzeAvailable =
    naturalHazardsEntry?.isRequired === true && (naturalHazardsEntry.files?.length ?? 0) > 0;
  vwgAntrag.extremwetterschutzSelbstbeteiligung =
    vwgCheckoutProdukt.extremwetterschutzSelbstbeteiligung;
  vwgAntrag.zuersZone = vwgCheckoutProdukt.zuersZone;
  if (isVwgV2) {
    vwgAntrag.hasGefahrenInVorversicherung = hasGefahrenInVorversicherung(
      produkt,
      state.vorversicherungen,
    );
  }
  addUnderwriterDecisionDataToVorschlagsdaten(vwgCheckoutProdukt, vwgAntrag, uwDescriptors);
}

type UnderwritingSubmissionAntrag = Partial<Record<UwSubmissionField, UnderwriterDecisionAntrag>>;

function addUnderwriterDecisionDataToVorschlagsdaten(
  checkoutProdukt: { uw?: ProductUw },
  antrag: UnderwritingSubmissionAntrag,
  descriptors: UwContextDescriptor[]
): void {
  const shared = checkoutProdukt.uw?.shared;

  descriptors.forEach(descriptor => {
    if (!descriptor.wire) {
      return;
    }
    const entry = selectUwEntries(checkoutProdukt.uw, descriptor.key)[0];
    applyUnderwriterDecisionAntrag(
      entry,
      shared,
      descriptor.wire.submissionField,
      antrag,
    );
  });
}

function applyUnderwriterDecisionAntrag(
  entry: UwContextEntry | undefined,
  shared: UwSharedDecision | undefined,
  submissionField: UwSubmissionField,
  antrag: UnderwritingSubmissionAntrag
): void {
  const decision = buildUwWireDecision(entry, shared);
  if (!decision) {
    return;
  }

  antrag[submissionField] = decision;

  if (shared.measures.length === 0) {
    return;
  }

  // `?? null` keeps a 0 % Zuschlag on the wire; the print mapper deliberately drops it with `|| null`
  antrag[submissionField].massnahmen = {
    zuschlag: findUwZuschlagProzent(shared) ?? null,
    vereinbarung: findUwVereinbarungText(shared) || null,
  };
}

export function addSpartenSpecificDataToVorschlagsdaten(
  vorschlagsdatenForProdukt: SparteAntrag,
  produkt: Produkt,
  state: IAppState
): void {
  if (vorschlagsdatenForProdukt.spartenType === SpartenType.WOHNGEBAEUDE
    || vorschlagsdatenForProdukt.spartenType === SpartenType.WOHNGEBAEUDE_V2) {
    handleVwgVorschlagsdaten(state, produkt, vorschlagsdatenForProdukt);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.HAUSRAT_V2) {
    addDefaultVersicherteGefahrenForHausrat(vorschlagsdatenForProdukt);
    handleVhvV2Vorschlagsdaten(vorschlagsdatenForProdukt as VhvV2DatenAntrag, state);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.HAUSRAT) {
    addDefaultVersicherteGefahrenForHausrat(vorschlagsdatenForProdukt);
    handleVhvVorschlagsdaten(vorschlagsdatenForProdukt as VhvDatenAntrag, state);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.PRIVATHAFTPFLICHT) {
    handlePhVorschlagsdaten(vorschlagsdatenForProdukt as PhDatenAntrag, state);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.RECHTSSCHUTZ) {
    handleRsVorschlagsdaten(vorschlagsdatenForProdukt as RsDatenAntrag, produkt, true);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.RECHTSSCHUTZ_VERMIETUNG) {
    handleRsVermieterVorschlagsdaten(vorschlagsdatenForProdukt as RsVermietungDatenAntrag, produkt);
  } else if (
    vorschlagsdatenForProdukt.spartenType === SpartenType.TIERHALTERHAFTPFLICHT_HUND ||
    vorschlagsdatenForProdukt.spartenType === SpartenType.TIERHALTERHAFTPFLICHT_PFERD
  ) {
    handleThhVorschlagsdaten(vorschlagsdatenForProdukt, state);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.TIERKRANKEN) {
    handleTkvVorschlagsdaten(
      vorschlagsdatenForProdukt as TkvDatenAntrag,
      state,
      produkt.angebotsId
    );
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.JAGDHAFTPFLICHT) {
    handleJhpVorschlagsdaten(vorschlagsdatenForProdukt as JhpDatenAntrag, state);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.UNFALL) {
    handleUriVorschlagsdaten(vorschlagsdatenForProdukt as UriDatenAntrag, state);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.GEGENSTANDSSCHUTZ) {
    handleGegeVorschlagsdaten(vorschlagsdatenForProdukt as GegeDatenAntrag, state);
  } else if (vorschlagsdatenForProdukt.spartenType === SpartenType.TIERKRANKEN_V2) {
    handleTkvV2Vorschlagsdaten(
      vorschlagsdatenForProdukt as TkvV2DatenAntrag,
      state,
      produkt
    );
  }
}

function handleTkvVorschlagsdaten(
  tkvDatenAntrag: TkvDatenAntrag,
  state: IAppState,
  angebotsId: string
): void {
  tkvDatenAntrag.tierAntrag = convertToBffTkvAntrag(state, angebotsId, tkvDatenAntrag.tierAntrag);
  for (const vorversicherungAntrag of tkvDatenAntrag.vorversicherung) {
    vorversicherungAntrag.versicherteGefahren = [BffVorschadenArt.TKV_OP, BffVorschadenArt.TKV_HB];
  }
}

function handleTkvV2Vorschlagsdaten(
  tkvDatenAntrag: TkvV2DatenAntrag,
  state: IAppState,
  produkt: Produkt
): void {
  tkvDatenAntrag.tierAntrag = convertToBffTkvV2Antrag(state, produkt.angebotsId);

  // Ersatzgeschaeft (contract replacement): carry the Vorsorgebonus pre-filled on the
  // Vertragsuebersicht (PS20-140094) straight through to the Einreichen payload, unchanged.
  const vorsorgebonusUebernahmeSumme = findVorsorgebonusUebernahmeSumme(produkt);
  if (vorsorgebonusUebernahmeSumme !== undefined) {
    tkvDatenAntrag.vorsorgebonusUebernahmeSumme = vorsorgebonusUebernahmeSumme;
  }
}

function findVorsorgebonusUebernahmeSumme(produkt: Produkt): number | undefined {
  return (produkt?.vorvertragsnummer ?? [])
    .map(vorvertrag => (vorvertrag.vorbelegungForBeibehaltung as TkvV2Vorbelegungdaten)?.vorsorgebonusUebernahmeSumme)
    .find(summe => summe !== undefined);
}

function handleJhpVorschlagsdaten(vorschlagsdatenForProdukt: SparteAntrag, state: IAppState): void {
  const jhpAngabenFilled = [
    state.jhpAngaben.jagdBehoerde,
    state.jhpAngaben.jagdschein,
    state.jhpAngaben.jagdBehoerde.ort,
    state.jhpAngaben.jagdBehoerde.behoerdename,
    state.jhpAngaben.jagdBehoerde.plz,
    state.jhpAngaben.jagdBehoerde.strasse,
    state.jhpAngaben.jagdBehoerde.hausnummer
  ]
    .map(Boolean)
    .every(Boolean);
  vorschlagsdatenForProdukt.jagdbehoerde = jhpAngabenFilled
    ? convertToBffJagdbehoerde(state.jhpAngaben)
    : null;
}

function handleUriVorschlagsdaten(
  vorschlagsdatenForProdukt: UriDatenAntrag,
  state: IAppState
): void {
  const vpAngabe: VpAngaben = state.unfAngaben.vpAngabenListe.find(
    angaben => angaben.angebotsId === vorschlagsdatenForProdukt.angebotsId
  );
  if (vpAngabe) {
    vorschlagsdatenForProdukt.hasDynamik = vpAngabe.dynamikSelected;
    vorschlagsdatenForProdukt.bezugsrecht = vpAngabe.bezugsrecht;
    vorschlagsdatenForProdukt.pflegebeduerftigt = vpAngabe.pflegeFrageAntwort;
    vorschlagsdatenForProdukt.beeintraechtigungen = vpAngabe.beeintraechtigungen;
    vorschlagsdatenForProdukt.bezugsrechtPersonen = [];
    vorschlagsdatenForProdukt.isSofortSchutzBesprochen = vpAngabe.isSofortschutzBesprochen;
    vpAngabe.bezugsberechtigten?.forEach(bezugsberechtigter => {
      vorschlagsdatenForProdukt.bezugsrechtPersonen.push({
        adresse: bezugsberechtigter.adresse,
        nachname: bezugsberechtigter.nachname,
        vorname: bezugsberechtigter.vorname,
        anrede: bezugsberechtigter.anrede,
        personenID: bezugsberechtigter.kdvId,
        geburtsdatum: convertDate(bezugsberechtigter.geburtsdatum),
        adelsbez: bezugsberechtigter.adelsbez,
        adelstitel: bezugsberechtigter.adelstitel,
        akadgrad: bezugsberechtigter.akadgrad,
        anteilBezugsrecht: bezugsberechtigter.anteil,
        firmenname: null,
        businessForm: null,
        juristisch: false,
        isVersicherungsnehmer:
          state.versicherungsnehmer.kundenverwaltungID === bezugsberechtigter.kdvId
      });
    });
  }
  vorschlagsdatenForProdukt.famRolle = state.beratung.assekuranzPersonenAngaben.find(
    angaben => angaben.angebotsId === vorschlagsdatenForProdukt.angebotsId
  )?.beziehungZumVn;
}

function handleThhVorschlagsdaten(vorschlagsdatenForProdukt: SparteAntrag, state: IAppState): void {
  if (vorschlagsdatenForProdukt.spartenType === SpartenType.TIERHALTERHAFTPFLICHT_HUND) {
    (vorschlagsdatenForProdukt as ThhHundDatenAntrag).tierAntrag =
      convertToBffTierHundAntrag(state);
    for (const vorversicherungAntrag of vorschlagsdatenForProdukt.vorversicherung) {
      vorversicherungAntrag.versicherteGefahren = [BffVorschadenArt.THH_HUND];
    }
    if (state.meldebehoerde.isMeldebehoerdeVorhanden) {
      vorschlagsdatenForProdukt.meldebehoerde = convertToBffMeldebehoerde(state.meldebehoerde);
    }
  }
  if (vorschlagsdatenForProdukt.spartenType === SpartenType.TIERHALTERHAFTPFLICHT_PFERD) {
    (vorschlagsdatenForProdukt as ThhPferdDatenAntrag).tierAntrag = convertToBffTierPferdAntrag(
      state.thhPferdAngaben
    );
    for (const vorversicherungAntrag of vorschlagsdatenForProdukt.vorversicherung) {
      vorversicherungAntrag.versicherteGefahren = [BffVorschadenArt.THH_PFERD];
    }
  }
}

function handleGegeVorschlagsdaten(
  vorschlagsdatenForProdukt: GegeDatenAntrag,
  state: IAppState
): void {
  const gegeProdukt = selectGegeProdukte(state).find(
    produkt => produkt.angebotsId === vorschlagsdatenForProdukt.angebotsId
  ) as GegeCheckoutProdukt;
  vorschlagsdatenForProdukt.gegenstaende = gegeProdukt.gegenstaende.map(gegenstand => {
    return {
      kategorieId: gegenstand.kategorie.name,
      marke: gegenstand.modell,
      sonderBeschreibung: gegenstand.informationen,
      kaufdatum: gegenstand.kaufdatum,
      kaufpreis: gegenstand.versicherungssumme
    };
  });
  const hasFahrrad = gegeProdukt.gegenstaende?.some(g => g.kategorie?.id === GegenstandCategory.FAHRRAD);
  const fahrradVorschaeden = gegeProdukt.fahrradVorschaeden;
  if (hasFahrrad && fahrradVorschaeden?.provided) {
    vorschlagsdatenForProdukt.vorschaeden = [
      { vorschadenArt: BffVorschadenArt.RAD, anzahl: fahrradVorschaeden.count, betrachtungszeitraum: 5 }
    ];
  } else {
    vorschlagsdatenForProdukt.vorschaeden = null;
  }
  for (const vorversicherungAntrag of vorschlagsdatenForProdukt.vorversicherung) {
    vorversicherungAntrag.versicherteGefahren = [BffVorschadenArt.RAD];
  }
}

export function handleVhvVorschlagsdaten(
  vorschlagsdatenForProdukt: VhvDatenAntrag,
  state: IAppState
): void {
  const vhvCheckoutProdukt: VhvCheckoutProdukt = getVhvCheckoutProdukt(state).find(
    produkt => produkt.angebotsId === vorschlagsdatenForProdukt.angebotsId
  );
  const hasWertsachen = vhvCheckoutProdukt && hoeherwertigerHausratUtils.checkIfShouldShowWersachensummeQuestion(
    vhvCheckoutProdukt,
  )
    ? vhvCheckoutProdukt?.hasWertsachen
    : undefined;
  vorschlagsdatenForProdukt.wohnung.wertsachen = {
    tresorSicherheitsstufen:
      hasWertsachen !== true ||
      !vhvCheckoutProdukt?.wertsachen?.verschlusspflichtige.tresoreVorhanden
        ? undefined
        : vhvCheckoutProdukt?.tresor?.eintraege
          .filter(tresor => tresor.sicherheitsstufe != null)
          .map(tresor => tresor.sicherheitsstufe),
    einbruchmeldeanlageVdsKlasse: vhvCheckoutProdukt?.einbruchMeldeanlage?.klasse,
    summeWertsachen: vhvCheckoutProdukt?.wertsachen?.summe,
    summeVerschlusspflichtigeWertsachen:
    vhvCheckoutProdukt?.wertsachen?.verschlusspflichtige?.summe,
    bargeld: vhvCheckoutProdukt?.wertsachen?.verschlusspflichtige?.bargeld
  };
  vorschlagsdatenForProdukt.wohnung.versicherungssumme = vhvCheckoutProdukt?.versicherungssumme;
  vorschlagsdatenForProdukt.wohnung.wertsachen.angebotWertsachenValue =
    vhvCheckoutProdukt?.wertsachenValue;
  vorschlagsdatenForProdukt.wohnung.wertsachen.hasWertsachen = hasWertsachen;
  vorschlagsdatenForProdukt.uploadedFilesIds = getVhvDocumentsIds(vhvCheckoutProdukt);
}

export function handleVhvV2Vorschlagsdaten(
  vorschlagsdatenForProdukt: VhvV2DatenAntrag,
  state: IAppState
): void {
  handleVhvVorschlagsdaten(vorschlagsdatenForProdukt as VhvDatenAntrag, state);

  const vhvV2CheckoutProdukt: VhvV2CheckoutProdukt = getVhvCheckoutProdukt(state).find(
    produkt => produkt.angebotsId === vorschlagsdatenForProdukt.angebotsId
  ) as VhvV2CheckoutProdukt;

  if (vhvV2CheckoutProdukt) {
    vorschlagsdatenForProdukt.hasGefahrenInVorversicherung = hasGefahrenInVorversicherung(
      vhvV2CheckoutProdukt,
      state.vorversicherungen,
    );
    const naturalHazardsEntry = selectUwEntries(vhvV2CheckoutProdukt.uw, UwContext.NATURAL_HAZARDS)[0];
    vorschlagsdatenForProdukt.elementarfragebogenFileToAnalyzeAvailable =
      naturalHazardsEntry?.isRequired === true && (naturalHazardsEntry.files?.length ?? 0) > 0;
    const uwDescriptors = applicableUwDescriptors(vorschlagsdatenForProdukt.spartenType);
    addUnderwriterDecisionDataToVorschlagsdaten(vhvV2CheckoutProdukt, vorschlagsdatenForProdukt, uwDescriptors);
  }

  if (vhvV2CheckoutProdukt?.zuersZone != null) {
    vorschlagsdatenForProdukt.zuersZone = Number.parseInt(vhvV2CheckoutProdukt.zuersZone, 10);
  }
  if (vhvV2CheckoutProdukt?.entschaedigungsbetragRad != null) {
    vorschlagsdatenForProdukt.entschaedigungsbetragRad = vhvV2CheckoutProdukt.entschaedigungsbetragRad;
  }
  if (vhvV2CheckoutProdukt?.tragerisiko != null) {
    vorschlagsdatenForProdukt.tragerisiko = {
      entschaedigungsgrenze: vhvV2CheckoutProdukt.tragerisiko.entschaedigungsgrenze,
      einzelwert: vhvV2CheckoutProdukt.tragerisiko.einzelwert,
    };
  }
  if (vhvV2CheckoutProdukt?.extremwetterschutzSelbstbeteiligung != null) {
    vorschlagsdatenForProdukt.extremwetterschutzSelbstbeteiligung =
      vhvV2CheckoutProdukt.extremwetterschutzSelbstbeteiligung;
  }

  if (selectFeatureToggle(state, Feature.VHV2X)) {
    addWertsachenFachbausteineToVorschlagsdaten(vorschlagsdatenForProdukt, vhvV2CheckoutProdukt);
  }
}


const HAUSRAT_SCHMUCK_SUMME = 'HH000301';
const HAUSRAT_SCHMUCK_EINZELWERT = 'HH000302';
const HAUSRAT_UHREN_SUMME = 'HH000177';
const HAUSRAT_UHREN_EINZELWERT = 'HH000304';
const HAUSRAT_KUNST_SUMME_PREMIUM = 'HH000307';
const HAUSRAT_KUNST_EINZELWERT = 'HH000308';

function addWertsachenFachbausteineToVorschlagsdaten(
  vorschlagsdatenForProdukt: VhvV2DatenAntrag,
  vhvV2CheckoutProdukt: VhvV2CheckoutProdukt
): void {
  const isPremium = vorschlagsdatenForProdukt.produkt.produktline === Produktlinienname.PREMIUM;
  const verschlusspflichtige = vhvV2CheckoutProdukt?.wertsachen?.verschlusspflichtige;
  const nichtVerschlusspflichtige = vhvV2CheckoutProdukt?.wertsachen?.nichtVerschlusspflichtige;
  const fachschluessel = vorschlagsdatenForProdukt.produkt.fachschluessel;
  const codesToRemove: string[] = [];

  if (isPremium) {
    const kunstSumme = nichtVerschlusspflichtige?.kunstgegenstaende;
    if (kunstSumme > 0) {
      setFachbausteinSumme(fachschluessel, HAUSRAT_KUNST_SUMME_PREMIUM, kunstSumme);
      setFachbausteinSumme(
        fachschluessel,
        HAUSRAT_KUNST_EINZELWERT,
        nichtVerschlusspflichtige?.kunstgegenstaendeEinzelwert
      );
    } else {
      codesToRemove.push(HAUSRAT_KUNST_SUMME_PREMIUM, HAUSRAT_KUNST_EINZELWERT);
    }

    const schmuckSumme = verschlusspflichtige?.schmucksachen;
    if (schmuckSumme > 0) {
      setFachbausteinSumme(fachschluessel, HAUSRAT_SCHMUCK_SUMME, schmuckSumme);
      setFachbausteinSumme(
        fachschluessel,
        HAUSRAT_SCHMUCK_EINZELWERT,
        verschlusspflichtige?.schmucksachenEinzelwert
      );
    } else {
      codesToRemove.push(HAUSRAT_SCHMUCK_SUMME, HAUSRAT_SCHMUCK_EINZELWERT);
    }

    const uhrenSumme = verschlusspflichtige?.armbandTaschenuhren;
    if (uhrenSumme > 0) {
      setFachbausteinSumme(fachschluessel, HAUSRAT_UHREN_SUMME, uhrenSumme);
      setFachbausteinSumme(
        fachschluessel,
        HAUSRAT_UHREN_EINZELWERT,
        verschlusspflichtige?.armbandTaschenuhrenEinzelwert
      );
    } else {
      codesToRemove.push(HAUSRAT_UHREN_SUMME, HAUSRAT_UHREN_EINZELWERT);
    }
  }

  vorschlagsdatenForProdukt.produkt.fachschluessel = fachschluessel.filter(
    fachbaustein => !codesToRemove.includes(fachbaustein.fachschluessel)
  );
}

function setFachbausteinSumme(
  fachschluessel: FachbausteinAntrag[],
  code: string,
  betrag: number
): void {
  const fachbaustein = fachschluessel.find(fb => fb.fachschluessel === code);
  if (fachbaustein) {
    fachbaustein.summe = String(betrag);
  }
}

export function handleTkvFrageAntwort(tkvAngaben: TkvAngabenElement): DiagnoseAntrag[] {
  const diagnosenAntrag: DiagnoseAntrag[] = new Array<DiagnoseAntrag>();
  tkvAngaben.diagnoseAngaben.forEach(diagnosen => {
    const diagnoseAntrag: DiagnoseAntrag = {
      diagnose: diagnosen.gewaehlteDiagnose.wert,
      diagnoseDetail: diagnosen.gewaehlteDiagnose.detail
        ? diagnosen.gewaehlteDiagnoseDetail.wert
        : null,
      diagnoseBeschreibung:
        diagnosen.gewaehlteDiagnose.wert === DIAGNOSE_SONSTIGES
          ? diagnosen.diagnoseBeschreibung
          : null,
      istBeiTierarzt: tkvAngaben.istBeiTierarzt
    };
    diagnosenAntrag.push(diagnoseAntrag);
  });
  return diagnosenAntrag;
}

export function convertToBffMeldebehoerde(meldebehoerde: MeldebehoerdeAngaben): Meldebehoerde {
  return {
    name: meldebehoerde.behoerdename,
    aktenzeichen: meldebehoerde.isAktenzeichenUnbekannt ? null : meldebehoerde.aktenzeichen,
    adresse: {
      ort: meldebehoerde.ort,
      plz: meldebehoerde.plz,
      strasse: meldebehoerde.strasse === '' ? null : meldebehoerde.strasse,
      hausnummer: meldebehoerde.hausnummer === '' ? null : meldebehoerde.hausnummer
    }
  };
}

export function convertToBffJagdbehoerde(jhpAngaben: JhpAngaben): Jagdbehoerde {
  return {
    name: jhpAngaben.jagdBehoerde.behoerdename,
    jagdschein: convertToBffJagdschein(jhpAngaben.jagdschein),
    adresse: {
      ort: jhpAngaben.jagdBehoerde.ort,
      plz: jhpAngaben.jagdBehoerde.plz,
      strasse: jhpAngaben.jagdBehoerde.strasse,
      hausnummer: jhpAngaben.jagdBehoerde.hausnummer
    }
  };
}

function convertToBffTkvAntrag(
  state: IAppState,
  angebotsId: string,
  tierAntragAusVorschlagsdaten: TierAntrag
): TierAntrag {
  const tkvAngaben: TkvAngabenElement = state.tkvAngaben.angabenListe.find(
    elem => elem.angebotsId === angebotsId
  );
  const diagnosenAntrag: DiagnoseAntrag[] = showTkvDiagnosen(state, tkvAngaben.angebotsId)
    ? handleTkvFrageAntwort(tkvAngaben)
    : null;
  const einsatzgebieteTier = tkvAngaben.einsatzgebiet;
  return {
    tierart: tkvAngaben.tierart,
    tiername: tkvAngaben.name,
    rasse: tkvAngaben.rasse.value,
    geburtsdatum: convertDate(tkvAngaben.geburtsdatum),
    geschlecht: tkvAngaben.geschlecht,
    chipnummer:
      tkvAngaben.kennzeichnungsArt === Kennzeichnungen.CHIP ? tkvAngaben.kennzeichnung : null,
    taetonummer:
      tkvAngaben.kennzeichnungsArt === Kennzeichnungen.TAETOWIERUNG
        ? tkvAngaben.kennzeichnung
        : null,
    lebensnummer:
      tkvAngaben.kennzeichnungsArt === Kennzeichnungen.LEBENSNUMMER
        ? tkvAngaben.kennzeichnung
        : null,
    tierhaltung: tkvAngaben.tierhaltung,
    isTierKastriert: tkvAngaben.isTierKastriert,
    einsatzgebieteTier,
    diagnosenAntrag:
      diagnosenAntrag || mapDiagnosenAntragFromVorschlagsdaten(tierAntragAusVorschlagsdaten, state)
  };
}

const mapSelectedDetailToCode = (
  selectedDetail: string,
  notInsuredScopes: HealthStatusEntry['notInsuredScopes']
): string | null => {
  if (!selectedDetail || !notInsuredScopes?.length) {
    return null;
  }
  const matchingScope = notInsuredScopes.find(scope => scope.scope === selectedDetail);
  return matchingScope?.code || null;
};

export function convertToBffTkvV2Antrag(state: IAppState, angebotsId: string): TierV2Antrag {
  const tkvAngaben: TkvAngabenV2Item = state.tkvAngabenV2.angabenItems.find(
    elem => elem.angebotsId === angebotsId
  );

  const diagnosenAntrag: DiagnoseAntrag[] = [];

  if (tkvAngaben.healthStatusEntries) {
    tkvAngaben.healthStatusEntries.forEach(entry => {
      const diagnosisCode = entry.notInsuredScopes?.[0]?.code;
      if (!diagnosisCode) {
        return;
      }

      const diagnoseAntrag: DiagnoseAntrag = {
        diagnose: diagnosisCode,
        diagnoseDetail: mapSelectedDetailToCode(
          entry.selectedDetails?.[0],
          entry.notInsuredScopes
        ),
        diagnoseBeschreibung: entry.description || null,
        istBeiTierarzt: true
      };
      diagnosenAntrag.push(diagnoseAntrag);
    });
  }


  return {
    geschlecht: tkvAngaben.animalGender,
    tierart: tkvAngaben.animalType,
    rasse: tkvAngaben.breed?.value,
    geburtsdatum: convertDate(tkvAngaben.animalBirthDate),
    tiername: tkvAngaben.animalName,
    isTierKastriert: false,
    einsatzgebieteTier: tkvAngaben.isAssistanceDog ? EinsatzgebieteTier.ASSISTENZHUND : null,
    chipnummer:
      tkvAngaben.animalIdentificationType === KennzeichnungsArt.CHIP ? tkvAngaben.animalIdentification : null,
    taetonummer:
      tkvAngaben.animalIdentificationType === KennzeichnungsArt.TAETOWIERUNG
        ? tkvAngaben.animalIdentification
        : null,
    lebensnummer:
      tkvAngaben.animalIdentificationType === KennzeichnungsArt.LEBENSNUMMER
        ? tkvAngaben.animalIdentification
        : null,
    tierhaltung: tkvAngaben.animalType === Tierart.VRKATZE ? tkvAngaben.catKeepingType : null,
    excludedBenefits: mapExcludedBenefits(tkvAngaben.healthStatusEntries, tkvAngaben.previousHealthStatusEntries),
    diagnosenAntrag: diagnosenAntrag.length > 0 ? diagnosenAntrag : null
  };
}

const mapExcludedBenefits = (
  healthStatusEntries: TkvAngabenV2Item['healthStatusEntries'],
  previousHealthStatusEntries: TkvAngabenV2Item['previousHealthStatusEntries']
): string[] => {
  const currentExclusions = (healthStatusEntries ?? [])
    .filter(entry => entry.notInsuredText)
    .flatMap(entry => {
      const treatmentStatus = entry.treatmentStatus?.length
        ? entry.treatmentStatus
        : [{ isHealed: null }];

      const excludedTreatments = treatmentStatus.filter(
        status => !status.isHealed || entry.isNotInsuredIfHealed
      );

      const hasDefaultScope = entry.notInsuredScopes?.some(scope => scope.scope === 'default');

      if (hasDefaultScope) {
        const defaultCode = entry.notInsuredScopes[0].code;
        return excludedTreatments.map(() => defaultCode);
      }

      return excludedTreatments.flatMap(treatment => {
        const matchingScope = entry.notInsuredScopes?.find(
          scope => scope.scope === treatment.selectedDetail
        );
        return matchingScope ? [matchingScope.code] : [];
      });
    });

  const previousExclusions = (previousHealthStatusEntries ?? [])
    .filter(entry => entry.notInsuredText)
    .flatMap(entry => {
      // Check if this entry uses a default scope (e.g., Sonstiges area)
      const hasDefaultScope = entry.notInsuredScopes?.some(scope => scope.scope === 'default');

      if (hasDefaultScope) {
        // For entries with default scope: generate the default code once
        return [entry.notInsuredScopes[0].code];
      }

      // For entries with specific scopes: map selected details to their matching codes
      const selectedDetails = entry.selectedDetails ?? [];
      return selectedDetails
        .map(detail => entry.notInsuredScopes?.find(scope => scope.scope === detail))
        .filter(scope => scope !== undefined)
        .map(scope => scope.code);
    });

  return [...currentExclusions, ...previousExclusions];
};

function mapDiagnosenAntragFromVorschlagsdaten(
  tierAntragAusVorschlagsdaten: TierAntrag,
  state: IAppState
): DiagnoseAntrag[] {
  const diagnosenDetailListe: Domaene[] = state.tkvAngaben?.diagnosenDetail;
  return tierAntragAusVorschlagsdaten?.diagnosenAntrag.map(diagnose => {
    const detailWert = diagnosenDetailListe.find(
      detail => detail.text === diagnose.diagnoseDetail
    )?.wert;
    return {
      ...diagnose,
      diagnoseDetail: detailWert || null,
      diagnose: diagnose.diagnose,
      istBeiTierarzt: true
    };
  });
}

function convertToBffTierHundAntrag(state: IAppState): TierAntrag[] {
  return state.thhHundAngaben.angabenListe.map(angabenHund => {
    const tierAntrag: TierAntrag = {
      tierart: Tierart.VRHUND,
      tiername: angabenHund.name,
      rasse: angabenHund.hunderasseValue,
      geburtsdatum: convertDate(angabenHund.geburtsdatum),
      geschlecht: convertToBffThhHundGeschlecht(angabenHund.geschlecht),
      chipnummer: angabenHund.chipnummer,
      taetonummer: angabenHund.taetowierungsnummer
    };
    return tierAntrag;
  });
}

function convertToBffTierPferdAntrag(thhPferdAngaben: ThhPferdAngaben): TierAntrag[] {
  return thhPferdAngaben.angabenListe.map(angabenPferd => {
    const tierAntrag: TierAntrag = {
      tierart: Tierart.VRPFERD,
      tiername: angabenPferd.name,
      rasse: angabenPferd.rasse,
      geburtsdatum: convertDate(angabenPferd.geburtsdatum),
      geschlecht: convertToBffThhPferdGeschlecht(angabenPferd.geschlecht),
      tiergroesse: angabenPferd.tiergroesse,
      chipnummer: angabenPferd.chipnummer,
      lebensnummer: angabenPferd.lebensnummer
    };
    return tierAntrag;
  });
}

function convertToBffThhHundGeschlecht(geschlecht: ThhHundGeschlecht): Tiergeschlecht {
  return geschlecht === ThhHundGeschlecht.M ? Tiergeschlecht.MAENNLICH : Tiergeschlecht.WEIBLICH;
}

function convertToBffThhPferdGeschlecht(geschlecht: ThhPferdGeschlecht): Tiergeschlecht {
  return geschlecht === ThhPferdGeschlecht.M ? Tiergeschlecht.MAENNLICH : Tiergeschlecht.WEIBLICH;
}

function handlePhVorschlagsdaten(vorschlagsdatenForProdukt: PhDatenAntrag, state: IAppState): void {
  if (vorschlagsdatenForProdukt.vorversicherung !== null) {
    addDefaultVersicherteGefahrenForHaftpflicht(vorschlagsdatenForProdukt);
  }
  vorschlagsdatenForProdukt.vermieteteWohnungen = convertToBffVermieteteWohnungen(
    state.vermieteteWohnungen
  );
}

function addDefaultVersicherteGefahrenForHaftpflicht(
  vorschlagsdatenForProdukt: SparteAntrag
): void {
  for (const vorversicherungAntrag of vorschlagsdatenForProdukt.vorversicherung) {
    vorversicherungAntrag.versicherteGefahren = [BffVorschadenArt.HA];
  }
}

function addDefaultVersicherteGefahrenForHausrat(vorschlagsdatenForProdukt: SparteAntrag): void {
  for (const vorversicherungAntrag of vorschlagsdatenForProdukt.vorversicherung) {
    if (!vorversicherungAntrag.versicherteGefahren?.length) {
      vorversicherungAntrag.versicherteGefahren = [BffVorschadenArt.RAD, BffVorschadenArt.FELS];
    }
  }
}

function handleRsVorschlagsdaten(
  vorschlagsdatenForProdukt: RsDatenAntrag,
  produkt: Produkt,
  wartezeitverzichtToggle: boolean
): void {
  if (vorschlagsdatenForProdukt.vorversicherung !== null) {
    addDefaultVersicherteGefahrenForRechtsschutz(vorschlagsdatenForProdukt);
  }
  if (vorschlagsdatenForProdukt.verkehrBaustein != null) {
    const rsCheckoutProdukt: RsCheckoutProdukt = produkt as RsCheckoutProdukt;
    vorschlagsdatenForProdukt.verkehrBaustein.kennzeichen = rsCheckoutProdukt.amtlichesKennzeichen;
    if (wartezeitverzichtToggle) {
      vorschlagsdatenForProdukt.wartezeitverzicht = { selected: true };
    }
  } else if (wartezeitverzichtToggle) {
    const rsCheckoutProdukt: RsCheckoutProdukt = produkt as RsCheckoutProdukt;
    vorschlagsdatenForProdukt.wartezeitverzicht = {
      selected: rsCheckoutProdukt.wartezeitverzicht.wartezeitverzichtSelected,
      referenzvertrag: rsCheckoutProdukt.wartezeitverzicht.referenzvertrag
    } as WartezeitverzichtAntrag;
  }
}

function handleRsVermieterVorschlagsdaten(
  vorschlagsdatenForProdukt: RsVermietungDatenAntrag,
  produkt: Produkt
): void {
  if (vorschlagsdatenForProdukt.vorversicherung !== null) {
    addDefaultVersicherteGefahrenForRechtsschutz(vorschlagsdatenForProdukt);
  }
  const rsVermieterCheckoutProdukt: RsVermietungCheckoutProdukt =
    produkt as RsVermietungCheckoutProdukt;
  vorschlagsdatenForProdukt.wartezeitverzicht = {
    selected: rsVermieterCheckoutProdukt.wartezeitverzicht.wartezeitverzichtSelected,
    referenzvertrag: rsVermieterCheckoutProdukt.wartezeitverzicht.referenzvertrag
  } as WartezeitverzichtAntrag;
}

function addDefaultVersicherteGefahrenForRechtsschutz(
  vorschlagsdatenForProdukt: SparteAntrag
): void {
  for (const vorversicherungAntrag of vorschlagsdatenForProdukt.vorversicherung) {
    vorversicherungAntrag.versicherteGefahren = [BffVorschadenArt.RS];
  }
}

function addZielmarktInfoToVorschlagsdaten(
  vorschlagsdatenForProdukt: SparteAntrag,
  produkt: Produkt
): void {
  vorschlagsdatenForProdukt.zielmarktAbweichung = produkt.abweichungVomZielmarkt;
  vorschlagsdatenForProdukt.zielmarktBegruendung = produkt.zielmarktBegruendung;
}

export function convertToBffSicherungsglaeubiger(
  sicherungsglaeubigerArray: Sicherungsglaeubiger[]
): BffSicherungsglaeubiger[] {
  return sicherungsglaeubigerArray.map(aSicherungsglaeubiger => {
    const bffSicherungsglaeubiger: BffSicherungsglaeubiger = {
      name: aSicherungsglaeubiger.name,
      aktenzeichen: aSicherungsglaeubiger.aktenzeichen,
      adresse: {
        ort: aSicherungsglaeubiger.ort,
        plz: aSicherungsglaeubiger.plz,
        strasse: aSicherungsglaeubiger.strasse === '' ? null : aSicherungsglaeubiger.strasse,
        hausnummer: aSicherungsglaeubiger.hausnr === '' ? null : aSicherungsglaeubiger.hausnr,
        postfach: aSicherungsglaeubiger.postfach === '' ? null : aSicherungsglaeubiger.postfach,
        grosskunde: isGrosskunde(aSicherungsglaeubiger)
      }
    };
    return bffSicherungsglaeubiger;
  });
}

function isGrosskunde(aSicherungsglaeubiger: Sicherungsglaeubiger): boolean {
  return (
    aSicherungsglaeubiger.postfach === '' &&
    (aSicherungsglaeubiger.strasse === '' || aSicherungsglaeubiger.hausnr === '')
  );
}

function convertToBffVermieteteWohnungen(
  vermieteteWohnungen: VermieteteWohnung[]
): BffVermieteteWohnung[] {
  const result: BffVermieteteWohnung[] = [];
  vermieteteWohnungen.forEach(vw => {
    const vwBff: BffVermieteteWohnung = {
      adresse: {
        plz: vw.plz,
        ort: vw.ort,
        strasse: vw.strasse,
        hausnummer: vw.hausnummer,
        tuernummer: vw.wohnungsnummer
      }
    };
    result.push(vwBff);
  });

  return result;
}

function convertToBffVorversicherungen(
  vorversicherungen: VorversicherungsItem[]
): BffVorversicherung[] {
  return vorversicherungen.map(vorversicherung => {
    const mappedVorversicherung: BffVorversicherung = {
      vsnr: vorversicherung.vsnr,
      gesellschaft: vorversicherung.versicherungsgesellschaft,
      versicherteGefahren: convertToBffVersicherteGefahren(vorversicherung.versicherteGefahren)
    };

    if (vorversicherung.ablaufdatum) {
      const newAblaufdatum = dayjs(vorversicherung.ablaufdatum, DATEFORMAT_LOCAL_STATE);
      if (newAblaufdatum.isValid()) {
        mappedVorversicherung.ablaufdatum = newAblaufdatum.format(DATEFORMAT_SHORT_ISO);
      }
    }

    if (vorversicherung.vomVorversichererGekuendigt !== undefined) {
      mappedVorversicherung.kuendiger = vorversicherung.vomVorversichererGekuendigt
        ? Kuendiger.VERSICHERER
        : Kuendiger.VERSICHERUNGSNEHMER;
    }

    return mappedVorversicherung;
  });
}

function convertToBffVersicherteGefahren(
  versicherteGefahren: VorversicherungsItem['versicherteGefahren']
): BffVorversicherung['versicherteGefahren'] {
  return versicherteGefahren
    .filter(versicherteGefahr => versicherteGefahr.selected)
    .map(versicherteGefahr => convertToBffVorschadenArt(versicherteGefahr.value));
}

function convertToBffJagdschein(jagdschein: Jagdschein): BffJagdschein {
  return {
    [Jagdschein.JAHRES_JAGDSCHEIN]: BffJagdschein.EIN_JAHRES_JAGDSCHEIN,
    [Jagdschein.DREI_JAHRES_JAGDSCHEIN]: BffJagdschein.DREI_JAHRES_JAGDSCHEIN
  }[jagdschein];
}

function convertToBffVorschadenArt(versicherteGefahr: VorschadenArt): BffVorschadenArt {
  return {
    [VorschadenArt.ELEMENTAR]: BffVorschadenArt.ELEMENTAR,
    [VorschadenArt.STURM_HAGEL]: BffVorschadenArt.STURM_HAGEL,
    [VorschadenArt.LEITUNGSWASSER]: BffVorschadenArt.LEITUNGSWASSER,
    [VorschadenArt.FEUER]: BffVorschadenArt.FEUER,
    [VorschadenArt.OPSCHUTZ]: BffVorschadenArt.OPSCHUTZ,
    [VorschadenArt.VOLLSCHUTZ]: BffVorschadenArt.VOLLSCHUTZ,
    [VorschadenArt.NICHT_BEKANNT_SCHUTZ]: BffVorschadenArt.NICHT_BEKANNT_SCHUTZ
  }[versicherteGefahr];
}

function addVnWechselGrundToVorvertragsnummern(
  vorschlagsdaten: SparteAntrag,
  state: IAppState
): void {
  if (state.vnWechselAngaben?.vnWechselGrundAngabenVorhanden) {
    const fremdVNVorvertraege: Array<FremdVNVorvertrag> =
      state.vnWechselAngaben.angabenListe.reduce(
        (acc, angabe) => acc.concat(angabe.fremdVNVorvertraege),
        []
      );

    vorschlagsdaten.vorvertragsnummern.forEach(vorvertragsnummerToUpdate => {
      const wechselGrund: VnWechselGrund = fremdVNVorvertraege
        .filter(
          vorvertrag =>
            vorvertrag.vorvertragsnummer.vertragsnummer ===
            vorvertragsnummerToUpdate.vorvertragsnummer &&
            compareProductIdsIfExistingAndSpLaufnrNotExists(
              vorvertrag.vorvertragsnummer.produktIds,
              vorvertragsnummerToUpdate.produktIds,
              vorvertrag.vorvertragsnummer.spLaufnummer,
              vorvertragsnummerToUpdate.spLaufnummer
            )
        )
        .reduce((acc, vorvertrag) => acc.concat(vorvertrag.vnWechselGrund), [])
        .find(grund => grund.appId === getAppEnumBySpartenType(vorschlagsdaten.spartenType));

      if (wechselGrund) {
        vorvertragsnummerToUpdate.fremdvertrag = true;
        vorvertragsnummerToUpdate.wechselGrund = wechselGrund.value;
        vorschlagsdaten.geschaeftsart = Vertragsgrund.ERSATZGESCHAEFT_VNWECHSEL;
      }
    });
  }
}
